import { useUrlGenerator } from '@folklore/routes';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useParams } from 'wouter';

import * as AppPropTypes from '../../lib/PropTypes';

import ExamQuestionGroupView from './Exam/QuestionGroupView';
import ExamQuestionView from './Exam/QuestionView';

import styles from '../../../styles/games/exam.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    reload: PropTypes.func,
};

const defaultProps = {
    game: null,
    reload: null,
};

function ExamGame({ game, reload }) {
    const { questionsGroups = null } = game;
    const url = useUrlGenerator();
    const { game: gameParam = null, question: questionParam = null } = useParams();
    const [currentView, setCurrentView] = useState(questionParam);

    useEffect(() => setCurrentView(questionParam), [questionParam]);

    const pages = useMemo(() => {
        const nextCurrentView = questionParam;
        return [currentView, nextCurrentView !== currentView ? nextCurrentView : null].filter(
            (it) => it !== null,
        );
    }, [questionParam, currentView]);

    const transitions = useTransition(pages, {
        initial: { opacity: 1 },
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    if (gameParam !== null && questionParam === null) {
        // pick the first question group that still has unanswered questions
        const firstQuestionGroup =
            game.questionsGroups.find(
                (qg) => qg.questions.find((q) => q.canAnswer === true) !== undefined,
            ) || game.questionsGroups[0];
        return (
            <Redirect
                to={url('game.question', {
                    game: game.id,
                    question: firstQuestionGroup.id,
                })}
            />
        );
    }

    return transitions((style, item) => {
        const questionsGroup =
            questionsGroups.find((qg) => {
                if (qg.id === item) {
                    return true;
                }
                return qg.questions.find((q) => q.id === item) !== undefined;
            }) || null;
        const question = questionsGroups.reduce((acc, qg) => {
            if (acc !== null) {
                return acc;
            }
            return qg.questions.find((q) => q.id === item) || null;
        }, null);

        const isLeaving = item !== currentView;

        return (
            <animated.div
                style={style}
                className={classNames({
                    [styles.isLeaving]: isLeaving,
                })}
            >
                {question !== null ? (
                    <ExamQuestionView questionsGroup={questionsGroup} question={question} />
                ) : (
                    <ExamQuestionGroupView questionsGroup={questionsGroup} reload={reload} />
                )}
            </animated.div>
        );
    });
}

ExamGame.propTypes = propTypes;
ExamGame.defaultProps = defaultProps;

export default ExamGame;
